/**
 * COM-840 — the `/resources/basket` loader's fail-soft contract.
 *
 * In the 2026-10-20 load test 855 basket GETs (`/resources/basket` and its
 * `.data` form) answered 500 after 8–12 s: the 8 s SCAPI `upstreamSignal` ran
 * out while Salesforce stalled, right after an add that had already succeeded.
 * COM-804 gave the basket ACTIONS a 503 + `upstreamTimeout` answer; the LOADER
 * still threw.
 *
 * The loader now answers an upstream timeout or an upstream server fault with
 * HTTP 200 and a STALE payload: the normal loader shape with `basket: null`
 * plus {@link BasketLoaderStaleMarker}. 200 rather than 503 because every
 * client reader treats this payload as "no answer" (below), and a non-2xx
 * would still count as a failed AddToCart step in the load test while buying
 * nothing on the client. It is never cached: `/resources/basket` is on the
 * COM-403 `SENSITIVE_PATH_PREFIXES` list, so `SensitiveNoStoreMiddleware`
 * stamps `private, no-store` on every response under it.
 *
 * Client contract — a stale payload is NOT a basket:
 *  - the keyed fetcher's `clientLoader` turns it into `null`, the contained-
 *    failure sentinel `BasketProvider` already handles: `selectBasketRaw`
 *    falls back to the last good keyed payload (then `softBasket`), and
 *    `useBasketInvalidationRefresh` counts it as a non-fresh settle, so the
 *    next attempt retries under the existing attempts cap and time valve;
 *  - the mount hydrate (`softBasket`) drops it, exactly like a failed fetch.
 * No toast is raised on either path: loader results never reach the
 * submission-event channel the failure toasts listen on.
 *
 * Programming errors (a `TypeError`, a broken invariant, a 4xx SCAPI fault
 * we did not expect) are NOT classified here and still throw to a 500.
 *
 * COM-902 — the stale payload is ONLY for a failed BASKET read (`getBasket` /
 * `getCustomerBaskets`). In the 2026-10-26 load test SCAPI `getProducts`
 * stalled past its 8 s budget in bursts, and the loader answered stale for a
 * basket that had already loaded with the just-added item: 329 "Expected item
 * in cart" failures. A soft failure of an ENRICHMENT read (product detail,
 * promotion callouts, the TPA purchase-limit check) now keeps the real basket,
 * substitutes that read's empty fallback and marks the payload PARTIAL
 * ({@link BasketLoaderPartialMarker}). Line count and subtotal come from
 * `basket.productItems`, so they stay right. Client contract — a partial
 * payload IS a basket, with gaps:
 *  - `BasketProvider` carries the last good value forward for each degraded
 *    part (`isPartialPart`), as it already does across action responses;
 *  - every product-detail gate keeps its own failure direction: the ET-393
 *    telehealth gate (`basketTelehealthState`) reads a line whose product is
 *    missing as UNRESOLVED and stays shut; the cosmetic readers fail open;
 *  - analytics (`gtmViewCart`) is withheld rather than sent with blank lines.
 * The same programming-error rule applies: only a classified upstream failure
 * degrades; anything else still rejects the load.
 */
import { isUpstreamTimeout } from "~/commerce-api/retry";
import { describeScapiError } from "~/commerce-api/scapi-fault";
import { upstreamTimeoutAttribution } from "~/utils/upstream-timeouts";

import type {
  BasketEnrichmentFailure,
  BasketEnrichmentPart,
  BasketLoaderPartialMarker,
  BasketLoaderSoftReason,
  BasketLoaderStaleMarker,
} from "./types";

/**
 * SCAPI fault-type slugs that mean "the server failed", not "your request was
 * wrong". An ALLOWLIST on purpose: the proxied SDK clients surface every fault
 * as a status-less `ScapiBodyError` (no `throwOnBadResponse`), so a type we do
 * not recognise must stay a real error rather than be guessed transient.
 * These slugs follow SCAPI's naming for the HTTP 5xx family; none has been
 * captured from a live stall yet (the 2026-10-20 failures were all timeouts).
 */
const SERVER_FAULT_SLUGS: ReadonlySet<string> = new Set([
  "internal-server-error",
  "bad-gateway",
  "service-unavailable",
  "gateway-timeout",
]);

/** HTTP status of the SDK's `ResponseError` shape (`{ response: Response }`). */
function responseStatusOf(error: unknown): number | undefined {
  if (!error || typeof error !== "object" || error instanceof Response) {
    // A thrown `Response` is our own control flow, never an upstream fault.
    return undefined;
  }
  const response = (error as { response?: { status?: unknown } }).response;
  return typeof response?.status === "number" ? response.status : undefined;
}

function faultSlugOf(error: unknown): string | undefined {
  const type = (error as { type?: unknown } | null)?.type;
  if (typeof type !== "string" || !type.includes("/errors/")) return undefined;
  return type.slice(type.lastIndexOf("/") + 1).toLowerCase();
}

/**
 * Should a basket-loader failure degrade to a stale payload? Returns the
 * reason when it should, `null` when the error must stay a real 500.
 */
export function classifyBasketLoaderFailure(
  error: unknown,
): BasketLoaderSoftReason | null {
  if (isUpstreamTimeout(error)) return "upstream-timeout";
  const status = responseStatusOf(error);
  if (status !== undefined && status >= 500 && status <= 599) {
    return "upstream-unavailable";
  }
  const slug = faultSlugOf(error);
  if (slug !== undefined && SERVER_FAULT_SLUGS.has(slug)) {
    return "upstream-unavailable";
  }
  return null;
}

/**
 * The attribution suffix for the loader's stale-payload warn (COM-886): which
 * budget timed out (`scapi:getBasket`, `slas`, ... — recorded by
 * `upstreamSignal`) with its armed and elapsed ms, or the HTTP status / SCAPI
 * fault for a server fault, plus the loader's own wall time. `budget` is
 * `"unattributed"` for a timeout no `upstreamSignal` produced.
 */
export function basketLoaderFailureDetail(
  error: unknown,
  loaderMs: number,
): string {
  const timeout = upstreamTimeoutAttribution(error);
  if (timeout) {
    return JSON.stringify({ ...timeout, loaderMs });
  }
  const { status, fault } = describeScapiError(error);
  return JSON.stringify({
    budget: isUpstreamTimeout(error) ? "unattributed" : null,
    status,
    fault,
    loaderMs,
  });
}

/** The marker fields the loader merges into its degraded payload. */
export function basketLoaderStaleMarker(
  reason: BasketLoaderSoftReason,
): BasketLoaderStaleMarker {
  return {
    stale: true,
    staleReason: reason,
    upstreamTimeout: reason === "upstream-timeout",
  };
}

/** True for a loader payload the server marked stale (see the file header). */
export function isStaleBasketPayload(data: unknown): boolean {
  return (
    data != null &&
    typeof data === "object" &&
    (data as { stale?: unknown }).stale === true
  );
}

/**
 * What the keyed "basket" fetcher should hold for a loader result: a stale
 * payload becomes `null`, the contained-failure sentinel (see the header).
 */
export function keyedBasketResult<T>(data: T | null): T | null {
  return isStaleBasketPayload(data) ? null : data;
}

/**
 * COM-902 — await one enrichment read of the basket loader. A failure that
 * {@link classifyBasketLoaderFailure} recognises (timeout, SCAPI server fault)
 * resolves to `fallback` and is recorded in `failures`; any other failure
 * rejects unchanged, so a programming error still reaches the loader's catch
 * and stays a 500.
 */
export async function settleBasketEnrichment<T>(
  part: BasketEnrichmentPart,
  work: Promise<T>,
  fallback: T,
  failures: BasketEnrichmentFailure[],
): Promise<T> {
  try {
    return await work;
  } catch (error) {
    const reason = classifyBasketLoaderFailure(error);
    if (!reason) throw error;
    console.warn(
      `[resources/basket] ${part} ${reason} — answering the basket with reduced detail`,
      error instanceof Error ? `${error.name}: ${error.message}` : error,
    );
    failures.push({ part, reason });
    return fallback;
  }
}

/** The partial-marker fields for a loader payload (see the file header). */
export function basketLoaderPartialMarker(
  failures: readonly BasketEnrichmentFailure[],
): BasketLoaderPartialMarker {
  return {
    partial: failures.length > 0,
    partialParts: [...new Set(failures.map(f => f.part))],
    partialReason: failures[0]?.reason ?? null,
  };
}

/**
 * Did the server degrade `part` of this loader payload? `false` for a complete
 * payload, an action response, or anything without the marker — so callers
 * treat the field as authoritative unless the server said otherwise.
 */
export function isPartialPart(
  data: unknown,
  part: BasketEnrichmentPart,
): boolean {
  if (data == null || typeof data !== "object") return false;
  const parts = (data as { partialParts?: unknown }).partialParts;
  return Array.isArray(parts) && parts.includes(part);
}

/**
 * Re-read the basket into the shared keyed "basket" fetcher: ONE loader run.
 *
 * This used to go through `failSoftReload`, which preflights the plain JSON
 * GET before calling `load()`. The preflight is a full basket loader run of
 * its own (`getBasket` + `getProducts` + `getPromotions`), so every
 * post-mutation refresh cost two (COM-840: the GET `/resources/basket` +
 * GET `/resources/basket.data` pair after each add in the load test). The
 * preflight existed to keep a WAF-challenge 403 on this XHR away from the
 * root error boundary (SFCC-11929); the route's `clientLoader` now contains
 * every rejected load as `null` (COM-63), so the guard is redundant here.
 */
export function reloadKeyedBasket(
  load: (href: string) => unknown,
  href: string,
): void {
  void load(href);
}
